<template>
  <div>
    <div
      v-for="grupo in tableData"
      :key="grupo.nomeSupervisor"
      class="flex flex-col bg-white shadow-lg p-6 rounded-lg mb-6"
    >
      <div class="flex flex-col sm:flex-row mb-10 items-center justify-between">
        <div class="w-full flex items-start justify-start gap-3">
          <div class="flex flex-col items-start justify-between mb-4">
            <h1 class="text-2xl sm:text-4xl title text-left sm:text-left">
              Equipe {{ grupo.nomeSupervisor.split(' ')[0] }}
            </h1>
            <div class="flex items-center justify-center gap-2">
              <input
                type="checkbox"
                :id="`show-inactive-${grupo.nomeSupervisor}`"
                v-model="grupo.showInactive"
                class="cursor-pointer"
              />
              <label
                :for="`show-inactive-${grupo.nomeSupervisor}`"
                class="cursor-pointer text-sm"
              >
                Mostrar Inativos
              </label>
            </div>
          </div>

          <div
            class="flex items-center justify-center rounded-full border-gold p-2 border border-solid"
            v-if="grupo.totalVendas >= grupo.metaTotal"
          >
            <AwardIcon style="width: 24px; height: 24px" class="golden" />
          </div>
        </div>
        <ProgressBarWithLegend
          class="mt-8 sm:mt-0"
          :finalDate="finalDate"
          :is-painel-geral="true"
          :supervisor="
            dadosSupervisores.find(
              ({ nomeSupervisor }) => nomeSupervisor === grupo.nomeSupervisor,
            )
          "
        />
      </div>
      <div
        class="mb-10 grid grid-cols-1 sm:grid-cols-2 md:flex md:items-center md:justify-between md:flex-wrap md:flex-1 gap-4"
      >
        <CardGeral title="Total de Vendas" :value="`${grupo.totalVendas}`" />
        <CardGeral
          v-if="grupo.valorPrimeiraParcela"
          title="Total 1ª Parcela"
          :value="`${grupo.valorPrimeiraParcela}`"
        />
        <CardGeral
          title="Inad 1ª Parcela"
          :value="`${grupo.inadPrimeiraParcela}`"
        />
        <CardGeral
          v-if="grupo.diferencaMeta"
          title="Diferença p/ Meta"
          :value="`${grupo.diferencaMeta}`"
        />
        <CardGeral
          title="Consultores - Meta 22"
          :value="`${countConsultoresWithMeta(grupo.consultores, 22)}`"
        />
        <CardGeral
          title="Consultores - Meta 11"
          :value="`${countConsultoresWithMeta(grupo.consultores, 11)}`"
        />
      </div>
      <div>
        <RankingTableV2
          :headers="tableHeaders"
          :data="filterConsultants(grupo)"
          :rankingField="rankingField"
          :order-descending="true"
          :is-painel-geral="true"
          :supervisor="dadosSupervisores"
          @openModal="handleOpenModal"
          @openCanceledModal="handleCanceledModal"
        />
        <ModalComponent
          fullscreen
          :is-visible.sync="isSalesBilletsModalVisible"
          :modal-data="salesBilletsData"
          title="Vendas Pagas"
        />
        <ModalComponent
          fullscreen
          :is-visible.sync="isModalVisible"
          :modal-data="openPixData"
          title="Pix em Aberto"
        />
        <ModalComponent
          fullscreen
          :is-visible.sync="isCanceledBilletsModalVisible"
          :modal-data="canceledBilletsData"
          title="Boletos Cancelados"
        />
      </div>
    </div>
  </div>
</template>

<script>
import RankingTableV2 from '../components/RankingTableV2.vue';
import { headers } from '../mocks/dataMocksTopConsultor';
import {
  calculateWorkingDays,
  totalWorkingDaysInDate,
} from '../utils/eh-dia-util';
import services from '../../../../services';
import { deepClone } from '../../../../utils/deepClone';
import ProgressBarWithLegend from '../components/ProgressBarWithLegend.vue';
import CardGeral from '../components/CardGeral.vue';
import { AwardIcon } from 'vue-feather-icons';
import ModalComponent from '../components/ModalComponent.vue';
import { getStartEndDate, getDateRange } from '../utils';

export default {
  name: 'PainelGeral',
  components: {
    RankingTableV2,
    ProgressBarWithLegend,
    CardGeral,
    AwardIcon,
    ModalComponent,
  },
  data() {
    return {
      tableHeaders: headers,
      tableData: [],
      rankingField: 'totalVendas',
      maxRows: 5,
      dadosSupervisores: [],
      openPixData: null,
      isModalVisible: false,
      isCanceledBilletsModalVisible: false,
      salesBilletsData: null,
      isSalesBilletsModalVisible: false,
      canceledBilletsData: null,
      temporaryTables: null,
      currentRelogio: null,
      totalRelogioMonth: null,
      showInactive: true,
    };
  },
  props: {
    previousData: Array,
    finalDate: String,
    isPresentationMode: Boolean,
    dataSend: Object,
  },
  watch: {},
  methods: {
    async fetchDataFromService(model, action) {
      const { startDate, endDate } = getDateRange(this.finalDate);
      this.currentRelogio = calculateWorkingDays(
        new Date(this.finalDate + 'T00:00:00'),
      );
      this.totalRelogioMonth = totalWorkingDaysInDate(
        new Date(this.finalDate + 'T00:00:00'),
      );
      const objSend = {
        ...this.dataSend,
        ...this.temporaryTables,
        currentRelogio: this.currentRelogio,
        totalRelogioMonth: this.totalRelogioMonth,
        startDate,
        endDate,
      };

      try {
        const res = await services.post(model, action, objSend);
        return res.data;
      } catch (error) {
        console.error(`Erro ao buscar dados: ${action}`, error);
        return null;
      }
    },
    async fetchData() {
      try {
        const equipeComercialData = await this.fetchDataFromService(
          'painel-comercial',
          'getEquipeComercial',
          { ...this.dataSend },
        );

        const hasNewData =
          equipeComercialData && equipeComercialData.length > 0;

        const processedData = hasNewData ? deepClone(equipeComercialData) : [];

        const topConsultores = await this.fetchTopConsultores();

        const mergedData = this.integrateTopConsultorData(
          processedData,
          topConsultores,
        );

        this.metaTotalSupervisores(mergedData);

        const shouldShowSupervisors = this.$can(
          'Visualizar Supervisor da Equipe em Painel Geral',
          'Super Ranking',
        );
        this.tableData = shouldShowSupervisors
          ? mergedData
          : this.filterSupervisorsFromConsultants(mergedData);

        this.tableData = mergedData.map((team) => ({
          ...team,
          showInactive: true,
        }));

        const dadosSupervisores = mergedData.map(
          ({ nomeSupervisor, totalVendas, metaTotal, relogio }) => ({
            nomeSupervisor,
            totalVendas,
            metaTotal,
            relogio,
          }),
        );
        this.dadosSupervisores = dadosSupervisores;
      } catch (error) {
        console.error('Erro ao buscar dados da equipe comercial:', error);
      }
    },
    filterSupervisorsFromConsultants(data) {
      return data.map((team) => {
        const { consultores, nomeSupervisor } = team;
        const filteredConsultores = consultores.filter(
          (consultor) => consultor.nome !== nomeSupervisor,
        );
        return {
          ...team,
          consultores: filteredConsultores,
        };
      });
    },
    async fetchTopConsultores() {
      const topConsultoresData = await this.fetchDataFromService(
        'painel-comercial',
        'getTopSupervisoresComercial',
        { ...this.temporaryTables, ...this.dataSend },
      );

      return topConsultoresData || [];
    },
    integrateTopConsultorData(mergedData) {
      return mergedData.map((supervisorGroup) => {
        const somaTotais = supervisorGroup.consultores.reduce(
          (soma, consultor) => {
            const valorPrimeiraParcela = consultor.valorPrimeiraParcela
              ? Number(
                  consultor.valorPrimeiraParcela
                    .replace('R$', '')
                    .replace(/\./g, '')
                    .replace(',', '.'),
                )
              : 0;

            return {
              metaTotal: soma.metaTotal + (consultor.metaTotal || 0),
              totalVendas: soma.totalVendas + consultor.totalVendas,
              relogio: soma.relogio + consultor.relogio,
              ticketMedio: soma.ticketMedio + consultor.ticketMedio,
              inadPrimeiraParcela:
                soma.inadPrimeiraParcela + consultor.inadPrimeiraParcela,
              valorPrimeiraParcela:
                soma.valorPrimeiraParcela + valorPrimeiraParcela,
            };
          },
          {
            metaTotal: 0,
            totalVendas: 0,
            relogio: 0,
            ticketMedio: 0,
            inadPrimeiraParcela: 0,
            valorPrimeiraParcela: 0,
          },
        );

        supervisorGroup.totalVendas = somaTotais.totalVendas;
        supervisorGroup.metaTotal = somaTotais.metaTotal;
        supervisorGroup.relogio = Math.round(
          (somaTotais.metaTotal / this.totalRelogioMonth) * this.currentRelogio,
        );
        supervisorGroup.ticketMedio = somaTotais.ticketMedio;
        supervisorGroup.inadPrimeiraParcela = somaTotais.inadPrimeiraParcela;

        supervisorGroup.diferencaMeta = 0;

        supervisorGroup.ticketMedio = String(supervisorGroup.ticketMedio);
        supervisorGroup.ticketMedio = supervisorGroup.ticketMedio.replace(
          /^0R\$/,
          'R$',
        );
        supervisorGroup.diferencaMeta =
          supervisorGroup.totalVendas - supervisorGroup.metaTotal;
        supervisorGroup.valorPrimeiraParcela = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(somaTotais.valorPrimeiraParcela);
        return supervisorGroup;
      });
    },
    sortByEfficiency(consultants) {
      return consultants.sort((a, b) => {
        const efficiencyA = a.relogio ? a.totalVendas / a.relogio : 0;
        const efficiencyB = b.relogio ? b.totalVendas / b.relogio : 0;

        return efficiencyB - efficiencyA;
      });
    },
    async handleOpenModal(rowData, onlyOpen = true) {
      this.$vs.loading();
      await this.createTemporaryTables();
      await this.fetchGetOpenBillets(rowData, onlyOpen);
      this.$vs.loading.close();
      this.dropTemporaryTables();
    },
    async handleCanceledModal(rowData) {
      this.$vs.loading();
      await this.createTemporaryTables();
      await this.fetchCanceledBillets(rowData);
      this.$vs.loading.close();
      this.dropTemporaryTables();
    },
    closeModal() {
      this.$emit('update:isModalVisible', false);
    },
    async fetchGetOpenBillets(openPixData, onlyOpen) {
      try {
        const { startDate, endDate } = getStartEndDate(this.finalDate);
        const convertToArray = openPixData.idUsuario.split(',');
        const idUsuario = [...new Set(convertToArray)];
        const isSupervisor = false;

        const res = await services.post('painel-comercial', 'getOpenBillets', {
          startDate,
          endDate,
          idUsuario,
          isSupervisor,
          onlyOpenSituation: onlyOpen,
          ...this.temporaryTables,
          ...this.dataSend,
        });

        if (onlyOpen) {
          this.openPixData = res.data;

          this.isModalVisible = true;
          return;
        }

        this.salesBilletsData = res.data;
        this.isSalesBilletsModalVisible = true;
      } catch (error) {
        console.error(
          'Erro ao buscar boletos abertos para melhores consultores:',
          error,
        );
      }
    },
    async fetchCanceledBillets(openPixData) {
      try {
        const { startDate, endDate } = getStartEndDate(this.finalDate);
        const convertToArray = openPixData.idUsuario.split(',');
        const idUsuario = [...new Set(convertToArray)];
        const isSupervisor = false;

        const res = await services.post(
          'painel-comercial',
          'getCanceledBillets',
          {
            startDate,
            endDate,
            idUsuario,
            isSupervisor,
            ...this.temporaryTables,
            ...this.dataSend,
          },
        );
        this.canceledBilletsData = res.data;
        this.isCanceledBilletsModalVisible = true;
      } catch (error) {
        console.error(
          'Erro ao buscar boletos cancelados para melhores consultores:',
          error,
        );
      }
    },
    countConsultoresWithMeta(consultores, metaValue) {
      const META_EXACT_MATCH = 22;
      const isMatchOrUndefined = (metaTotal) =>
        metaValue === META_EXACT_MATCH
          ? metaTotal === metaValue
          : metaTotal === metaValue || metaTotal == null;

      const filteredConsultores = consultores.filter(
        ({ metaTotal, dataInativacao }) =>
          isMatchOrUndefined(metaTotal) && !dataInativacao,
      );

      return filteredConsultores.length;
    },
    countEmptyChairConsultores(grupoSupervisores) {
      const metaSupervisores = grupoSupervisores.metaTotal;
      const META_NEW_CONSULTANT = 11;

      const somaMetaConsultores = grupoSupervisores.consultores.reduce(
        (soma, consultor) => {
          return (
            soma +
            (consultor.metaTotal === null
              ? META_NEW_CONSULTANT
              : consultor.metaTotal)
          );
        },
        0,
      );

      const diferencaMeta = metaSupervisores - somaMetaConsultores;

      const cadeirasVazias = Math.max(
        0,
        Math.ceil(diferencaMeta / META_NEW_CONSULTANT),
      );

      return cadeirasVazias;
    },
    async createTemporaryTables() {
      try {
        const { startDate, endDate } = getStartEndDate(this.finalDate);

        const { data } = await services.post(
          'painel-comercial',
          'createTemporaryTables',
          {
            startDate,
            endDate,
            ...this.dataSend,
          },
        );
        this.temporaryTables = data;
      } catch (error) {
        console.error('Erro ao criar tabelas tempor:', error);
      }
    },
    async dropTemporaryTables() {
      await services.post(
        'painel-comercial',
        'dropTemporaryTables',
        this.temporaryTables,
      );
      this.temporaryTables = null;
    },
    metaTotalSupervisores(supevisores) {
      const dateWithTime = this.finalDate + 'T00:00:00';
      const date = new Date(dateWithTime);
      const currentRelogio = calculateWorkingDays(date);
      const totalRelogioMonth = totalWorkingDaysInDate(date);

      const somaMetaSupervisores = supevisores.reduce(
        (soma, supervisor) => soma + (supervisor.metaTotal || 0),
        0,
      );

      const somaVendasSupervisores = supevisores.reduce(
        (soma, supervisor) => soma + (supervisor.totalVendas || 0),
        0,
      );

      const totalRelogio = Math.round(
        (somaMetaSupervisores / totalRelogioMonth) * currentRelogio,
      );
      const divisao = {
        somaMetaSupervisores,
        somaVendasSupervisores,
        totalRelogio,
      };
      this.$emit('metaTotalSupervisores', divisao);
    },
    filterConsultants(team) {
      if (!team.showInactive) {
        return team.consultores.filter(
          (consultant) => !consultant.dataInativacao,
        );
      }
      return team.consultores;
    },
  },
  async mounted() {
    await this.createTemporaryTables();
    await this.fetchData();
    this.dropTemporaryTables();
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400&display=swap');

.title {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  color: #434343;
}
.icon {
  width: 36px;
  height: 36px;
}
.golden {
  color: #d7a546;
}
.border-gold {
  border-color: #d7a546;
}
</style>
